import { ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import Alert, { AlertVariant } from "@galaxy-io/dls/feedback/Alert";
import Fieldset from "@galaxy-io/dls/inputs/Fieldset";
import TextAreaInput from "@galaxy-io/dls/inputs/TextAreaInput";
import Flex, { JustifyContent } from "@galaxy-io/dls/layout/Flex";
import Text, { TextSize, TextVariant } from "@galaxy-io/dls/text/Text";
import type { DreamMap, DreamNode, DreamPerson } from "@gtm/contracts/core/dream-list";
import { dreamMapInput } from "@gtm/contracts/core/dream-list";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { QueryState } from "@/components/query-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfirmAction } from "@/components/workspace/confirm-action";
import { SelectField } from "@/components/workspace/select-field";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { apiClient, unwrap } from "@/lib/api-client";

export function NodeEditor({
  map,
  node,
  onClose,
  onSave,
  onRemove,
}: {
  map: DreamMap;
  node: DreamNode;
  onClose: () => void;
  onSave: (node: DreamNode) => Promise<void>;
  onRemove?: () => Promise<void>;
}) {
  const client = useQueryClient();
  const [removing, setRemoving] = useState(false);
  const [draft, setDraft] = useState(node);
  const [mode, setMode] = useState(node.personId ? "existing" : "placeholder");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const people = useQuery({
    queryKey: ["dream-people", map.id],
    queryFn: ({ signal }) =>
      unwrap(
        apiClient().GET("/dream-list/{id}/people", { params: { path: { id: map.id } }, signal }),
      ),
  });
  const candidates = (people.data ?? []).filter(
    (person) => !map.nodes.some((n) => n.id !== node.id && n.personId === person.id),
  );
  const descendants = new Set([node.id]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const n of map.nodes)
      if (n.reportsTo && descendants.has(n.reportsTo) && !descendants.has(n.id)) {
        descendants.add(n.id);
        changed = true;
      }
  }
  async function save() {
    setError(undefined);
    setBusy(true);
    try {
      let next = { ...draft };
      // Validate the chart before creating a new directory person.
      dreamMapInput.parse({ ...map, nodes: [...map.nodes.filter((n) => n.id !== node.id), next] });
      if (mode === "new") {
        const person = await unwrap(
          apiClient().POST("/people", {
            body: {
              name: name.trim(),
              companyId: map.companyId,
              publicEmail: email.trim() || null,
              firstName: null,
              lastName: null,
              location: null,
              website: null,
              bio: null,
            },
          }),
        );
        next = { ...next, personId: person.id, name: name.trim(), email: email.trim() || null };
        setDraft(next);
        setMode("existing");
        client.setQueryData<DreamPerson[]>(["dream-people", map.id], (items = []) => [
          ...items,
          { id: person.id, name: next.name!, email: next.email },
        ]);
        void client.invalidateQueries({ queryKey: ["people"] });
      }
      await onSave(next);
      onClose();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <SurfaceDialog
      open
      onClose={onClose}
      title={map.nodes.some((n) => n.id === node.id) ? "Edit position" : "Add a person or role"}
      dirty={JSON.stringify(draft) !== JSON.stringify(node) || !!name || !!email}
      busy={busy}
      headerDescription={`Build the team at ${map.name}.`}
      footer={
        <Flex
          gap={8}
          width="100%"
          justifyContent={onRemove ? JustifyContent.SPACE_BETWEEN : JustifyContent.END}
        >
          {onRemove && (
            <Button
              label="Remove position"
              variant={ButtonVariant.TERTIARY}
              isDisabled={busy}
              onClick={() => setRemoving(true)}
            />
          )}
          <Button
            label="Save position"
            isLoading={busy}
            isDisabled={
              !draft.role.trim() ||
              (mode === "existing" && !draft.personId) ||
              (mode === "new" && !name.trim())
            }
            onClick={() => void save()}
          />
        </Flex>
      }
    >
      <Fieldset label="Person">
        <SelectField
          label="Position type"
          value={mode}
          disabled={busy}
          onChange={(value) => {
            setMode(value);
            setDraft({ ...draft, personId: null, name: null, email: null });
          }}
          options={[
            { value: "placeholder", label: "Role to find" },
            { value: "existing", label: "Person from this company" },
            { value: "new", label: "Create a new person" },
          ]}
        />
        {mode === "existing" &&
          (people.isPending || people.error ? (
            <QueryState error={people.error} onRetry={() => void people.refetch()} />
          ) : (
            <>
              <SelectField
                label="Person"
                search
                value={draft.personId || ""}
                placeholder="Choose a person"
                disabled={busy}
                options={candidates.map((person) => ({ value: person.id, label: person.name }))}
                onChange={(id) => {
                  const person = candidates.find((p) => p.id === id);
                  if (person)
                    setDraft({ ...draft, personId: id, name: person.name, email: person.email });
                }}
              />
              {!candidates.length && (
                <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
                  No unmapped people at this company yet. Create a new person or keep a role to
                  find.
                </Text>
              )}
            </>
          ))}
        {mode === "new" && (
          <>
            <Input label="Name" value={name} onChange={setName} disabled={busy} />
            <Input
              label="Email (optional)"
              type="email"
              value={email}
              onChange={setEmail}
              disabled={busy}
            />
            <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
              This person will also be added to your People directory and linked to {map.name}.
            </Text>
          </>
        )}
      </Fieldset>
      <Fieldset label="Position">
        <Input
          label="Role"
          placeholder="e.g. VP Engineering"
          value={draft.role}
          disabled={busy}
          onChange={(role) => setDraft({ ...draft, role })}
        />
        <Input
          label="Department (optional)"
          placeholder="e.g. Engineering"
          value={draft.department}
          disabled={busy}
          onChange={(department) => setDraft({ ...draft, department })}
        />
        <SelectField
          label="Reports to"
          value={draft.reportsTo || ""}
          disabled={busy}
          onChange={(id) => setDraft({ ...draft, reportsTo: id || null })}
          options={[
            { value: "", label: "No manager mapped" },
            ...map.nodes
              .filter((n) => !descendants.has(n.id))
              .map((n) => ({ value: n.id, label: `${n.name || "Unknown person"} · ${n.role}` })),
          ]}
        />
      </Fieldset>
      <TextAreaInput
        label="Notes (optional)"
        value={draft.notes}
        isDisabled={busy}
        onChange={(notes) => setDraft({ ...draft, notes })}
        placeholder="What matters to them? Who could introduce you?"
      />
      <ConfirmAction
        isOpen={removing}
        onOpenChange={setRemoving}
        header="Remove position"
        label="Remove position"
        isDestructive
        description="Remove this position from the map? Direct reports will become unassigned. The person stays in your directory."
        onConfirm={async () => {
          await onRemove?.();
          onClose();
        }}
      />
      {error && <Alert variant={AlertVariant.ERROR}>{error}</Alert>}
    </SurfaceDialog>
  );
}
